import React from 'react'
import ModalShell from './ui/ModalShell'
import ModalFooter from './ui/ModalFooter'
import { buttonVariants } from './ui/buttonVariants'

const ExclamationTriangleIcon: React.FC<{ className?: string }> = ({ className = 'h-5 w-5' }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
  </svg>
)

interface FinancialValidationModalProps {
  isOpen: boolean
  onClose: () => void
  errorType: 'skip_funding' | 'invalid_transition'
  currentFlag: string
  attemptedFlag: string
}

const FinancialValidationModal: React.FC<FinancialValidationModalProps> = ({
  isOpen,
  onClose,
  errorType,
  currentFlag,
  attemptedFlag,
}) => {
  const getErrorMessage = () => {
    switch (errorType) {
      case 'skip_funding':
        return {
          title: 'Deal Must Be Funded First',
          message: 'This deal cannot be moved to compliance status because it has not been funded yet. Deals must go through the funding process before moving to compliance.',
          instruction: 'Please move the deal to a "Closed" status first to fund it, then you can move it to compliance.',
        }
      case 'invalid_transition':
        return {
          title: 'Invalid Financial Transition',
          message: `This deal cannot transition from "${currentFlag}" to "${attemptedFlag}" status. Deals must follow the proper financial progression.`,
          instruction: 'Please follow the correct financial progression: Pipeline → Funded → Open Revenue → Paid.',
        }
      default:
        return {
          title: 'Invalid Financial Status Change',
          message: "This status change is not allowed based on the current financial state of the deal.",
          instruction: "Please check the deal's current financial status and try again.",
        }
    }
  }

  const errorInfo = getErrorMessage()

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <span className="flex items-center justify-center w-8 h-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
          </span>
          {errorInfo.title}
        </span>
      }
    >
      <div className="space-y-4">
        <p className="text-gray-600 dark:text-gray-400">{errorInfo.message}</p>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">Financial Progression Steps:</h3>
          <ol className="space-y-1 text-sm text-blue-700 dark:text-blue-300 list-none">
            {['Pipeline (initial state)', 'Funded (when moved to "Closed" status)', 'Open Revenue (when moved to "Compliance" status)', 'Paid (when moved to "Archived" status)'].map((step, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0" />
                {i + 1}. {step}
              </li>
            ))}
          </ol>
        </div>

        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{errorInfo.instruction}</p>

        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-sm">
          <div className="flex justify-between mb-1">
            <span className="text-gray-500 dark:text-gray-400">Current Financial Status:</span>
            <span className="font-medium capitalize">{currentFlag.replace('_', ' ')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Attempted Status:</span>
            <span className="font-medium capitalize">{attemptedFlag.replace('_', ' ')}</span>
          </div>
        </div>
      </div>

      <ModalFooter>
        <button onClick={onClose} className={buttonVariants.primary}>
          I Understand
        </button>
      </ModalFooter>
    </ModalShell>
  )
}

export default FinancialValidationModal
